import React, { useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddEditPopup from './AddEditPopup';
import { formatDate } from '../../utils/Utils';
import { BeatLoader } from "react-spinners";
import { useFetchOrganizations, useDeleteOrganization } from '../../hooks/useOrganizations';
import AddEditOrganizationPopup from './AddEditOrganizationPopup';

function OrganizationManagementTable({
  search,
  setSearch,
  setLastLogin,
  click,
  setClick,
  sortedBy,
  setSortedBy
}) {
  const [searchInput, setSearchInput] = useState(search); // for typing
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const { data, error, isLoading } = useFetchOrganizations(currentPage, search, sortedBy, setSortedBy);
  const deleteUserMutation = useDeleteOrganization();

  const handleEdit = (id) => {
    const customer = data.users.find(c => c.id === id);
    setSelectedCustomer(customer);
    setShowPopup(true);
  };

  const handleFilterChange = (event) => {
    setSearchInput(event.target.value); 
  };

  const handleSearchClick = () => {
    setSearch(searchInput); 
    setClick(!click);
    setLastLogin(false);
  };

  const clearFilter = () => {
    setSearchInput('');
    setSearch('');
    setClick(!click);
    setLastLogin(false);
  };

  const handleDelete = (id) => {
    deleteUserMutation.mutate(id);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) return <div className='col-span-full flex justify-center items-center'><BeatLoader /></div>;
  if (error) return <div className='col-span-full flex justify-center items-center'>Error loading Organizations.</div>;

  return (
    <div className="col-span-full bg-white shadow-lg rounded-sm border border-slate-200">
      <header className="px-5 py-4 border-b border-slate-100">
        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-slate-800">Organizations</h2>
          <div className="flex gap-2">
            <input
              type="text"
              className="form-input w-64 border border-slate-300 rounded px-3 py-1 text-sm"
              placeholder="Search organizations..."
              value={searchInput}
              onChange={handleFilterChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); 
                  handleSearchClick(); 
                }
              }}
            />
            <button
              className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
              onClick={handleSearchClick}
            >
              Search
            </button>
            <button
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded hover:bg-gray-400"
              onClick={clearFilter}
            >
              Clear
            </button>
          </div>
        </div>
      </header>

      <div className="p-3">
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead className="text-xs font-semibold uppercase text-slate-400 bg-slate-50">
              <tr>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Name</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Industry</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Country</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Creation Date</div></th>
                <th className="p-2 whitespace-nowrap"><div className="font-semibold text-left">Updated Date</div></th>
                {/* <th className="p-2 whitespace-nowrap"><div className="font-semibold text-center">Action</div></th> */}
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-100">
              {data?.organizations?.map(customer => (
                <tr key={customer.id}>
                  <td className="p-2 whitespace-nowrap"><div className="text-left">{customer?.name}</div></td>
                  <td className="p-2 whitespace-nowrap"><div className="text-left">{customer?.industry}</div></td>
                  <td className="p-2 whitespace-nowrap"><div className="text-left">{customer?.country || "-"}</div></td>
                  <td className="p-2 whitespace-nowrap"><div className="text-left">{!customer.createdAt ? "" : formatDate(customer.createdAt)}</div></td>
                  <td className="p-2 whitespace-nowrap"><div className="text-left">{!customer.updatedAt ? "" : formatDate(customer.updatedAt)}</div></td>

                   {/* Uncomment below if you want edit/delete buttons */}
                  {/* <td className="p-2 whitespace-nowrap">
                    <div className="text-lg text-center flex items-center justify-center gap-2">
                      <button className="text-green-500 hover:text-green-700" onClick={() => handleEdit(customer.id)}>
                        <FaEdit />
                      </button>
                      <button className="text-red-500 hover:text-red-700" onClick={() => handleDelete(customer.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center mt-4">
          <button
            className="px-3 min-w-[100px] py-1 bg-gray-200 rounded-l-lg hover:bg-gray-300"
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span className="px-3 py-1">Page {currentPage + 1} of {data?.totalPages}</span>
          <button
            className="px-3 min-w-[100px] py-1 bg-gray-200 rounded-r-lg hover:bg-gray-300"
            disabled={currentPage === data?.totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>

        {showPopup && (
          <AddEditOrganizationPopup customer={selectedCustomer} onClose={() => setShowPopup(false)} />
        )}
      </div>
    </div>
  );
}

export default OrganizationManagementTable;
